import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Select, MenuItem, Menu, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';

const SearchTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  borderColor: theme.palette.grey[700],
  borderRadius: '6px',
  '& input': {
    color: theme.palette.grey[700],
  },
  width: '100%',
}));

const FilterSelect = styled(Select)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  borderColor: theme.palette.grey[700],
  borderRadius: '6px',
  color: theme.palette.grey[700],
  height: '38px',
  minWidth: '150px',
}));

const SearchButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  backgroundColor: theme.palette.primary.main,
  boxShadow: 'none',
  '&:hover': {
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
  },
}));

const FilterButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  gap: '6px',
}));

const MainSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '12px',
  paddingRight: '20px',
});

const FilterSection = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  gap: '10px',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const FilterMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const TableFilters = ({
  setLoad,
  setPage,
  filterCmpId,
  filterUserId,
  filterStatus,
  filterPlanId,
  filterPlanType,
  filterSearchUserId,
  filterInvoiceId,
  filterTStart,
  filterTEnd,
  filterType,
  orderBy,
  order,
  onFilterChange,
  onApplyFilters,
  onSortChange,
  filterMode = 'campaign'
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Local state for filter inputs
  const [localFilterCmpId, setLocalFilterCmpId] = useState(filterCmpId);
  const [localFilterUserId, setLocalFilterUserId] = useState(filterUserId);
  const [localFilterStatus, setLocalFilterStatus] = useState(filterStatus);
  const [localFilterPlanId, setLocalFilterPlanId] = useState(filterPlanId);
  const [localFilterPlanType, setLocalFilterPlanType] = useState(filterPlanType);
  const [localFilterSearchUserId, setLocalFilterSearchUserId] = useState(filterSearchUserId);
  const [localFilterInvoiceId, setLocalFilterInvoiceId] = useState(filterInvoiceId);
  const [localFilterTStart, setLocalFilterTStart] = useState(filterTStart);
  const [localFilterTEnd, setLocalFilterTEnd] = useState(filterTEnd);
  const [localFilterType, setLocalFilterType] = useState(filterType);
  const [localOrder, setLocalOrder] = useState(order);

  // Sync local state with parent props when they change
  useEffect(() => {
    if (filterMode === 'credit') {
      setLocalFilterSearchUserId(filterSearchUserId);
      setLocalFilterPlanId(filterPlanId);
      setLocalFilterTStart(filterTStart);
      setLocalFilterTEnd(filterTEnd);
      setLocalFilterType(filterType);
    }
    else if (filterMode === 'amount') {
      setLocalFilterSearchUserId(filterSearchUserId);
      setLocalFilterInvoiceId(filterInvoiceId);
      setLocalFilterTStart(filterTStart);
      setLocalFilterTEnd(filterTEnd);
      setLocalFilterType(filterType);
    } else {
      setLocalFilterCmpId(filterCmpId);
      setLocalFilterUserId(filterUserId);
      setLocalFilterStatus(filterStatus);
      setLocalFilterPlanId(filterPlanId);
      setLocalFilterTStart(filterTStart);
      setLocalFilterTEnd(filterTEnd);
    }
    setLocalOrder(order);
  }, [filterCmpId, filterUserId, filterStatus, filterPlanId, filterSearchUserId, filterTStart, filterTEnd, filterType, order, filterMode]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearch = () => {
    // Apply filters when clicking "Apply Filters" button
    if (filterMode === 'credit') {
      onFilterChange('searchUserId', localFilterSearchUserId);
      onFilterChange('planId', localFilterPlanId);
      onFilterChange('type', localFilterType);
      onFilterChange('tStart', localFilterTStart);
      onFilterChange('tEnd', localFilterTEnd);
    } else if (filterMode === 'amount') {
      onFilterChange('searchUserId', localFilterSearchUserId);
      onFilterChange('invoiceId', localFilterInvoiceId);
      onFilterChange('type', localFilterType);
      onFilterChange('tStart', localFilterTStart);
      onFilterChange('tEnd', localFilterTEnd);
    } else {
      onFilterChange('cmpId', localFilterCmpId);
      onFilterChange('userId', localFilterUserId);
      onFilterChange('status', localFilterStatus);
      onFilterChange('planId', localFilterPlanId);
      onFilterChange('planType', localFilterPlanType);
      onFilterChange('tStart', localFilterTStart);
      onFilterChange('tEnd', localFilterTEnd);
    }
    onSortChange(orderBy, localOrder);
    if (onApplyFilters) {
      onApplyFilters();
    } else {
      setPage(0);
      setLoad(true);
    }
    handleClose();
  };

  const hasActiveFilters = filterMode === 'credit' || filterMode === 'amount'
    ? (filterSearchUserId || filterPlanId || filterType || filterTStart || filterTEnd || orderBy !== 'create_date' || order !== 'desc')
    : (filterCmpId || filterUserId || filterStatus || filterPlanId || filterPlanType || filterTStart || filterTEnd || orderBy !== 'create_date' || order !== 'desc');

  const handleClearFilters = () => {
    if (filterMode === 'credit') {
      setLocalFilterSearchUserId('');
      setLocalFilterPlanId('');
      setLocalFilterType('');
      setLocalFilterTStart('');
      setLocalFilterTEnd('');
      setLocalOrder('desc');
      
      onFilterChange('searchUserId', '');
      onFilterChange('planId', '');
      onFilterChange('type', '');
      onFilterChange('tStart', '');
      onFilterChange('tEnd', '');
      onSortChange('create_date', 'desc');
      if (onApplyFilters) {
        onApplyFilters();
      } else {
        setPage(0);
        setLoad(true);
      }
    } else if (filterMode === 'amount') {
      setLocalFilterSearchUserId('');
      setLocalFilterInvoiceId('');
      setLocalFilterType('');
      setLocalFilterTStart('');
      setLocalFilterTEnd('');
      setLocalOrder('desc');
      
      onFilterChange('searchUserId', '');
      onFilterChange('invoiceId', '');
      onFilterChange('type', '');
      onFilterChange('tStart', '');
      onFilterChange('tEnd', '');
      onSortChange('create_date', 'desc');
      if (onApplyFilters) {
        onApplyFilters();
      } else {
        setPage(0);
        setLoad(true);
      }
    } else {
      setLocalFilterCmpId('');
      setLocalFilterUserId('');
      setLocalFilterStatus('');
      setLocalFilterPlanId('');
      setLocalFilterPlanType('');
      setLocalFilterTStart('');
      setLocalFilterTEnd('');
      setLocalOrder('desc');
      
      onFilterChange('cmpId', '');
      onFilterChange('userId', '');
      onFilterChange('status', '');
      onFilterChange('planId', '');
      onFilterChange('planType', '');
      onFilterChange('tStart', '');
      onFilterChange('tEnd', '');
      onSortChange('create_date', 'desc');
      if (onApplyFilters) {
        onApplyFilters();
      } else {
        setPage(0);
        setLoad(true);
      }
    }
  };

  return (
    <MainSection>
      <FilterSection>
        {/* <Count>Total Invoice</Count> */}
        <Box></Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
            {hasActiveFilters && (
            <Chip
              icon={<ClearIcon />}
              label="Clear Filters"
              onClick={handleClearFilters}
              variant="outlined"
              size="small"
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
              }}
            />
          )}
          <FilterButton
            onClick={handleClick}
            size="medium"
            title="More Filters"
            startIcon={<FilterAltIcon sx={{ color: 'grey.700' }} />}
          >
            Filter
          </FilterButton>
        </Box>
      </FilterSection>

      <FilterMenu
        id="filter-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: '450px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'grey.700' }}>
            Filters
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filterMode === 'credit' ? (
              <>
                {/* Credit Invoice Filters */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    User ID
                  </Typography>
                  <SearchTextField
                    type="text"
                    id="filterSearchUserId"
                    onChange={(e) => setLocalFilterSearchUserId(e.target.value)}
                    value={localFilterSearchUserId}
                    size="small"
                    placeholder="Enter User ID"
                    variant="outlined"
                    fullWidth
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    Plan ID
                  </Typography>
                  <SearchTextField
                    type="text"
                    id="filterPlanId"
                    onChange={(e) => setLocalFilterPlanId(e.target.value)}
                    value={localFilterPlanId}
                    size="small"
                    placeholder="Enter Plan ID"
                    variant="outlined"
                    fullWidth
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    Type
                  </Typography>
                  <FilterSelect
                    value={localFilterType}
                    onChange={(e) => setLocalFilterType(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="A">Add</MenuItem>
                    <MenuItem value="D">Deduct</MenuItem>
                  </FilterSelect>
                </Box>
              </>
            ):filterMode === 'amount' ? ( <>
                {/* Amount Invoice Filters */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    User ID
                  </Typography>
                  <SearchTextField
                    type="text"
                    id="filterSearchUserId"
                    onChange={(e) => setLocalFilterSearchUserId(e.target.value)}
                    value={localFilterSearchUserId}
                    size="small"
                    placeholder="Enter User ID"
                    variant="outlined"
                    fullWidth
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    Invoice ID
                  </Typography>
                  <SearchTextField
                    type="text"
                    id="filterInvoiceId"
                    onChange={(e) => setLocalFilterInvoiceId(e.target.value)}
                    value={localFilterInvoiceId}
                    size="small"
                    placeholder="Enter Invoice ID"
                    variant="outlined"
                    fullWidth
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    Type
                  </Typography>
                  <FilterSelect
                    value={localFilterType}
                    onChange={(e) => setLocalFilterType(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="A">Add</MenuItem>
                    <MenuItem value="D">Deduct</MenuItem>
                  </FilterSelect>
                </Box>
              </>) : (
              <>
                {/* Campaign Invoice Filters */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    Campaign ID
                  </Typography>
                  <SearchTextField
                    type="text"
                    id="filterCmpId"
                    onChange={(e) => setLocalFilterCmpId(e.target.value)}
                    value={localFilterCmpId}
                    size="small"
                    placeholder="Enter Campaign ID"
                    variant="outlined"
                    fullWidth
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    User ID
                  </Typography>
                  <SearchTextField
                    type="text"
                    id="filterUserId"
                    onChange={(e) => setLocalFilterUserId(e.target.value)}
                    value={localFilterUserId}
                    size="small"
                    placeholder="Enter User ID"
                    variant="outlined"
                    fullWidth
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    Plan ID
                  </Typography>
                  <SearchTextField
                    type="text"
                    id="filterPlanId"
                    onChange={(e) => setLocalFilterPlanId(e.target.value)}
                    value={localFilterPlanId}
                    size="small"
                    placeholder="Enter Plan ID"
                    variant="outlined"
                    fullWidth
                  />
                </Box>

                {/* <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    Status
                  </Typography>
                  <FilterSelect
                    value={localFilterStatus}
                    onChange={(e) => setLocalFilterStatus(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="0">Started</MenuItem>
                    <MenuItem value="1">Complete</MenuItem>
                    <MenuItem value="2">Deactivated</MenuItem>
                  </FilterSelect>
                </Box> */}

                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                    Plan Type
                  </Typography>
                  <FilterSelect
                    value={localFilterPlanType}
                    onChange={(e) => setLocalFilterPlanType(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">Select Types</MenuItem>
                    <MenuItem value="A">Amount</MenuItem>
                    <MenuItem value="C">Credit</MenuItem>
                   
                  </FilterSelect>
                </Box>
              </>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                  Start Date
                </Typography>
                <SearchTextField
                  type="date"
                  id="filterTStart"
                  onChange={(e) => setLocalFilterTStart(e.target.value)}
                  value={localFilterTStart}
                  size="small"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                  End Date
                </Typography>
                <SearchTextField
                  type="date"
                  id="filterTEnd"
                  onChange={(e) => setLocalFilterTEnd(e.target.value)}
                  value={localFilterTEnd}
                  size="small"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 0.5 }}>
                Sort Order
              </Typography>
              <FilterSelect
                value={localOrder}
                onChange={(e) => setLocalOrder(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="desc">Latest First</MenuItem>
                <MenuItem value="asc">Oldest First</MenuItem>
              </FilterSelect>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
              <SearchButton
                variant="contained"
                size="small"
                onClick={() => handleSearch()}
                fullWidth
              >
                Apply Filters
              </SearchButton>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                fullWidth
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Box>
      </FilterMenu>

      {/* <Count>Total Invoice: {count}</Count> */}
    </MainSection>
  );
};

export default TableFilters;
